import { useSortable, SortableTh } from "@/components/common/SortableTable";
import { useEffect, useMemo, useState } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { api, fmtCurrency, fmtNumber } from "@/lib/api";
import { TID } from "@/constants/testIds";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

const STATUS_STYLE = {
  OPERATIONAL: "bg-emerald-50 text-emerald-700 border-emerald-200",
  MAINTENANCE_DUE: "bg-amber-50 text-amber-700 border-amber-200",
  FAULT: "bg-rose-50 text-rose-700 border-rose-200",
  DECOMMISSIONED: "bg-slate-100 text-slate-500 border-slate-200",
};

const PIE_COLORS = ["#00334C", "#004a6b", "#0ea5e9", "#f59e0b", "#e11d48"];

export default function AssetsPage() {
  const [assets, setAssets] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [category, setCategory] = useState("all");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/assets").then((r) => setAssets(r.data)),
      api.get("/assets-stats/summary").then((r) => setSummary(r.data)),
    ]).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return assets.filter((a) => {
      if (status !== "all" && a.status !== status) return false;
      if (category !== "all" && a.category !== category) return false;
      if (ql && !`${a.name} ${a.site} ${a.client} ${a.id}`.toLowerCase().includes(ql)) return false;
      return true;
    });
  }, [assets, q, status, category]);
  const { sorted: sortedAssets, sort: assetSort, setSort: setAssetSort } = useSortable(filtered);

  const categories = useMemo(() => Array.from(new Set(assets.map((a) => a.category))), [assets]);

  const openAsset = async (id) => {
    const { data } = await api.get(`/assets/${id}`);
    setSelected(data);
  };

  return (
    <AppLayout title="Asset Management" subtitle="Live from Aroflo — jobs, sites and equipment">
      {loading ? (
        <div className="h-64 bg-white border border-slate-200 rounded-lg shimmer" />
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
              {[
                { label: "Total assets", value: fmtNumber(summary.total_count) },
                { label: "Total value", value: fmtCurrency(summary.total_value) },
                { label: "Operational", value: fmtNumber(summary.by_status.find((s) => s.name === "OPERATIONAL")?.value || 0) },
                { label: "Needs attention", value: fmtNumber((summary.by_status.find((s) => s.name === "MAINTENANCE_DUE")?.value || 0) + (summary.by_status.find((s) => s.name === "FAULT")?.value || 0)) },
              ].map((s) => (
                <div key={s.label} className="bg-white border border-slate-200 rounded-lg p-5">
                  <div className="text-xs uppercase tracking-wider text-slate-500">{s.label}</div>
                  <div className="mt-2 font-display text-2xl font-semibold text-slate-900 tabular-nums">{s.value}</div>
                </div>
              ))}
            </div>

            <div className="bg-white border border-slate-200 rounded-lg p-5">
              <h3 className="font-display text-sm font-semibold text-slate-900 mb-2">By category</h3>
              <div className="h-40">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={summary.by_category}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={38}
                      outerRadius={64}
                      paddingAngle={2}
                    >
                      {summary.by_category.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, borderColor: "#e2e8f0" }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <ul className="mt-1 space-y-1 text-xs">
                {summary.by_category.map((c, i) => (
                  <li key={c.name} className="flex items-center justify-between">
                    <span className="flex items-center gap-2 text-slate-600">
                      <span className="w-2.5 h-2.5 rounded-sm" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                      {c.name}
                    </span>
                    <span className="text-slate-800 font-medium tabular-nums">{c.value}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Filters + Table */}
          <div className="mt-6 bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="p-4 flex flex-wrap items-center gap-3 border-b border-slate-100">
              <div className="relative flex-1 min-w-[220px]">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  data-testid={TID.assetSearch}
                  placeholder="Search asset, site, client…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  className="pl-9 h-9 bg-slate-50 border-slate-200"
                />
              </div>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid={TID.assetFilterStatus} className="w-[180px] h-9">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="OPERATIONAL">Operational</SelectItem>
                  <SelectItem value="MAINTENANCE_DUE">Maintenance due</SelectItem>
                  <SelectItem value="FAULT">Fault</SelectItem>
                  <SelectItem value="DECOMMISSIONED">Decommissioned</SelectItem>
                </SelectContent>
              </Select>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid={TID.assetFilterCategory} className="w-[160px] h-9">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  {categories.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
                </SelectContent>
              </Select>
              <div className="ml-auto text-xs text-slate-500">{filtered.length} shown</div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    {[["Asset", "name"], ["Category", "category"], ["Site / Client", "site"], ["Status", "status"], ["Last service", "last_service"], ["Next service", "next_service"], ["Value", "value"]].map(([c, k]) => (
                      <SortableTh key={k} label={c} k={k} sort={assetSort} setSort={setAssetSort} className="text-left font-medium px-5 py-2.5" />
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedAssets.map((a) => (
                    <tr
                      key={a.id}
                      data-testid={TID.assetRow(a.id)}
                      onClick={() => openAsset(a.id)}
                      className="border-b border-slate-100 hover:bg-slate-50/70 transition-colors cursor-pointer"
                    >
                      <td className="px-5 py-3">
                        <div className="font-medium text-slate-800">{a.name}</div>
                        <div className="text-[11px] text-slate-400 font-mono">{a.id}</div>
                      </td>
                      <td className="px-5 py-3 text-slate-600">{a.category}</td>
                      <td className="px-5 py-3">
                        <div className="text-slate-800">{a.site}</div>
                        <div className="text-[11px] text-slate-400">{a.client}</div>
                      </td>
                      <td className="px-5 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full border text-[11px] font-medium ${STATUS_STYLE[a.status]}`}>
                          {a.status.replace("_", " ")}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-slate-600 tabular-nums">{a.last_service || "—"}</td>
                      <td className="px-5 py-3 text-slate-600 tabular-nums">{a.next_service || "—"}</td>
                      <td className="px-5 py-3 tabular-nums text-slate-800 font-medium">{fmtCurrency(a.value)}</td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={7} className="text-center py-10 text-slate-500 text-sm">No assets match your filters.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="w-full sm:max-w-[520px] overflow-y-auto">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="font-display">{selected.name}</SheetTitle>
                <SheetDescription>
                  <span className="font-mono text-xs text-slate-500">{selected.id}</span> · {selected.category}
                </SheetDescription>
              </SheetHeader>
              <div className="mt-5 grid grid-cols-2 gap-4">
                <Info label="Status" value={<span className={`inline-flex px-2 py-0.5 rounded-full border text-[11px] font-medium ${STATUS_STYLE[selected.status]}`}>{selected.status.replace("_", " ")}</span>} />
                <Info label="Value" value={fmtCurrency(selected.value)} />
                <Info label="Site" value={selected.site} />
                <Info label="Client" value={selected.client} />
                <Info label="Last service" value={selected.last_service} />
                <Info label="Next service" value={selected.next_service || "—"} />
              </div>
              <div className="mt-6">
                <h4 className="text-sm font-medium text-slate-900 mb-2">History</h4>
                <ul className="space-y-2">
                  {selected.history.map((h, i) => (
                    <li key={i} className="text-sm border-l-2 border-slate-200 pl-3 py-1">
                      <div className="text-slate-800">{h.event}</div>
                      <div className="text-[11px] text-slate-500">{h.date} · {h.tech}</div>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mt-6">
                <h4 className="text-sm font-medium text-slate-900 mb-2">Related jobs</h4>
                {selected.related_jobs.length === 0 ? (
                  <p className="text-sm text-slate-500">No related jobs.</p>
                ) : (
                  <ul className="space-y-2">
                    {selected.related_jobs.map((j) => (
                      <li key={j.id} className="flex items-center justify-between text-sm border border-slate-200 rounded-md px-3 py-2">
                        <div>
                          <div className="font-medium text-slate-800">{j.id}</div>
                          <div className="text-[11px] text-slate-500">{j.tech} · {j.scheduled}</div>
                        </div>
                        <span className="text-xs px-2 py-0.5 rounded-full border border-slate-200 text-slate-600">{j.status}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </AppLayout>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-1 text-sm text-slate-900">{value}</div>
    </div>
  );
}
